import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useLandProgress } from '../land/LandProgressContext'
import { AuthIdentity } from './AuthIdentity'
import { Icon } from './Icon'
import { PingPongLandLogo } from './PingPongLandLogo'

function PortalHeader({ onHome }: { onHome?: () => void }) {
  return (
    <header className="portal-header">
      <button className="portal-brand" type="button" onClick={onHome} aria-label="핑퐁랜드 선택 화면">
        <PingPongLandLogo />
      </button>
      <AuthIdentity className="portal-profile" eyebrow="WELCOME" iconSize={17} />
    </header>
  )
}

type PortalProps = {
  onAutonomous: () => void
  onMission: () => void
  onWorld: () => void
}

type MissionTrackId = 'assembly' | 'coding' | 'applied'

type MissionStage = {
  title: string
  objective: string
  rules: readonly [string, string]
  playable?: boolean
  serverCode?: string
}

type ExtraMission = {
  title: string
  objective: string
}

type MissionTrack = {
  id: MissionTrackId
  name: string
  level: string
  eyebrow: string
  description: string
  stages: readonly MissionStage[]
  extras: readonly ExtraMission[]
}

const MISSION_TRACKS: readonly MissionTrack[] = [
  {
    id: 'assembly',
    name: '조립 미션',
    level: '초급',
    eyebrow: 'BUILD',
    description: '연결, 균형, 접지와 무게중심을 단계별로 익히며 목적에 맞는 로봇을 만듭니다.',
    stages: [
      { title: '첫 번째 장애물', objective: '8개 이하의 부품으로 로봇을 조립해 장애물을 밀고 도착선을 통과하세요.', rules: ['부품 8개 이하', '도착선 통과'], playable: true, serverCode: 'LAND_BASIC_01' },
      { title: '안정적인 바퀴', objective: '좌우 바퀴와 지지 구조를 배치해 요철 구간을 전복 없이 완주하세요.', rules: ['부품 10개 이하', '전복 0회'] },
      { title: '경사로 구조', objective: '구동력과 무게중심을 조절해 가파른 경사로의 정상까지 올라가세요.', rules: ['부품 12개 이하', '경사로 통과'] },
      { title: '빈틈 건너기', objective: '차체 길이와 바퀴 간격을 조절해 끊어진 바닥 구간을 안전하게 건너세요.', rules: ['부품 12개 이하', '낙하 금지'] },
      { title: '좁은 통로', objective: '로봇의 폭을 줄이고 회전 공간을 확보해 좁은 통로를 빠져나가세요.', rules: ['폭 180mm 이하', '벽 충돌 2회 이하'] },
      { title: '무게중심', objective: '부품의 위치를 조절해 좌우로 흔들리는 코스에서도 자세를 유지하세요.', rules: ['부품 14개 이하', '기울기 25° 이하'] },
      { title: '견인 로봇', objective: '연결부가 끊어지지 않는 구조를 만들고 100g 화물을 도착점까지 끌어가세요.', rules: ['부품 15개 이하', '100g 화물 운반'] },
      { title: '충돌 보호', objective: '충돌 에너지를 분산하는 보호 구조로 핵심 큐브를 지키며 벽을 통과하세요.', rules: ['부품 16개 이하', '핵심 큐브 보호'] },
      { title: '복합 구조', objective: '경사, 요철, 좁은 길이 이어진 세 구간을 하나의 구조로 연속 통과하세요.', rules: ['부품 18개 이하', '3구간 연속 통과'] },
      { title: '조립 마스터', objective: '배운 원리를 모두 활용해 제한 시간 안에 종합 코스를 완주하세요.', rules: ['부품 20개 이하', '모든 평가 통과'] },
    ],
    extras: [
      { title: '최소 부품 챌린지', objective: '가장 적은 부품으로 첫 번째 장애물을 해결하세요.' },
      { title: '초경량 타임어택', objective: '무게를 줄인 로봇으로 기본 코스의 기록을 단축하세요.' },
    ],
  },
  {
    id: 'coding',
    name: '코딩 미션',
    level: '중급',
    eyebrow: 'CODE',
    description: '모터 명령부터 반복과 조건 판단까지 순서대로 익히며 로봇의 움직임을 설계합니다.',
    stages: [
      { title: '첫 모터', objective: '1번 큐브의 모터를 정방향으로 회전시키고 정확한 위치에서 정지하세요.', rules: ['모터 명령 2개', '정방향 회전'] },
      { title: '직진과 정지', objective: '좌우 모터의 속도를 맞춰 직선 구간을 벗어나지 않고 도착점에 멈추세요.', rules: ['좌우 속도 일치', '정지선 준수'] },
      { title: '속도 제어', objective: '저속과 고속 구간을 나누어 장애물을 건드리지 않고 제한 시간 안에 완주하세요.', rules: ['속도 2단계', '제한 시간 20초'] },
      { title: '정확한 회전', objective: '모터 방향을 반대로 제어해 지정된 지점에서 90도로 회전하세요.', rules: ['오차 ±5°', '회전 명령 사용'] },
      { title: '시간 명령', objective: '대기 시간을 조합해 세 개의 표시 구간에 순서대로 정차하세요.', rules: ['정차 3회', '시간 블록 사용'] },
      { title: '반복 주행', objective: '반복 블록으로 사각 코스를 한 바퀴 돌고 출발 위치로 돌아오세요.', rules: ['반복 블록 필수', '블록 12개 이하'] },
      { title: '조건 분기', objective: '장애물 감지 결과에 따라 좌우 경로 중 비어 있는 길을 선택하세요.', rules: ['조건 블록 필수', '충돌 0회'] },
      { title: '센서 반응', objective: '센서 값의 변화에 반응해 속도를 낮추고 목표물 앞에 정지하세요.', rules: ['센서 입력 사용', '간격 50mm 이하'] },
      { title: '코스 알고리즘', objective: '반복과 조건을 함께 사용해 갈림길이 있는 복합 코스를 완주하세요.', rules: ['블록 24개 이하', '모든 체크포인트'] },
      { title: '코딩 마스터', objective: '주어진 로봇을 코딩해 세 가지 무작위 코스에 연속으로 대응하세요.', rules: ['재시작 0회', '3코스 연속 성공'] },
    ],
    extras: [
      { title: '최소 블록 직진', objective: '가장 적은 블록으로 정확한 직진과 정지를 구현하세요.' },
      { title: '10초 정밀 주행', objective: '출발부터 정지까지 정확히 10초가 되도록 속도를 설계하세요.' },
    ],
  },
  {
    id: 'applied',
    name: '응용 미션',
    level: '고급',
    eyebrow: 'APPLY',
    description: '조립과 코딩을 함께 활용해 환경을 판단하고 스스로 문제를 해결하는 로봇을 완성합니다.',
    stages: [
      { title: '구조와 동작', objective: '주행 중 자세가 무너지지 않도록 구조와 모터 명령을 함께 조정하세요.', rules: ['조립 + 코딩', '전복 0회'] },
      { title: '정밀 운반', objective: '깨지기 쉬운 화물을 흔들림 없이 집어 지정된 위치까지 운반하세요.', rules: ['화물 낙하 금지', '오차 50mm 이하'] },
      { title: '장애물 판단', objective: '장애물 크기를 판별해 밀기와 우회 중 더 적합한 행동을 선택하세요.', rules: ['행동 2종 이상', '충돌 제한 준수'] },
      { title: '경로 탐색', objective: '막힌 길을 기억하고 되돌아가 출구까지 이어지는 경로를 찾으세요.', rules: ['경로 재탐색', '제한 시간 60초'] },
      { title: '균형 제어', objective: '기울기 변화에 맞춰 모터 출력을 보정하며 흔들리는 다리를 건너세요.', rules: ['출력 자동 보정', '낙하 금지'] },
      { title: '협동 장치', objective: '두 개의 구동 장치를 순서에 맞게 작동시켜 큰 화물을 옮기세요.', rules: ['장치 2개 연동', '화물 200g'] },
      { title: '속도와 토크', objective: '평지에서는 빠르게, 경사에서는 강하게 움직이도록 주행 모드를 전환하세요.', rules: ['주행 모드 2개', '모터 과부하 금지'] },
      { title: '복합 센서', objective: '거리와 기울기 정보를 함께 사용해 낙하 구간과 벽을 피하세요.', rules: ['센서 2종 이상', '충돌 0회'] },
      { title: '자율 주행', objective: '출발 이후 조작 없이 모든 체크포인트를 판단해 종합 코스를 완주하세요.', rules: ['수동 조작 금지', '체크포인트 5개'] },
      { title: '응용 마스터', objective: '제한된 부품과 코드로 처음 보는 코스를 분석하고 가장 빠르게 해결하세요.', rules: ['부품·블록 제한', '종합 점수 80점'] },
    ],
    extras: [
      { title: '랜덤 장애물', objective: '매번 달라지는 장애물 배치를 스스로 판단해 통과하세요.' },
      { title: '배틀봇 타임어택', objective: '배틀봇을 개조하고 코딩해 종합 코스 최고 기록에 도전하세요.' },
    ],
  },
]

export function LandPortal({ onAutonomous, onMission, onWorld }: PortalProps) {
  return (
    <section className="portal-page portal-home">
      <img
        className="portal-world-backdrop"
        src="/assets/activity-cards/home-cloud-landscape-v2.jpg"
        alt=""
        aria-hidden="true"
      />
      <AuthIdentity
        className="portal-floating-profile"
        ariaLabel="사용자 계정"
        compact
      />
      <div className="portal-content">
        <div className="portal-hero-row">
          <div className="portal-intro">
            <h1>상상한 로봇이<br /><em>하나의 세계</em>가 됩니다.</h1>
            <p>직접 만들고, 움직임을 설계하고, 함께 플레이하세요.<br />모든 경험은 하나의 로봇에서 시작됩니다.</p>
          </div>
        </div>

        <div className="experience-grid experience-grid-polished" aria-label="핑퐁랜드 활동 선택">
          <button className="experience-card autonomous" type="button" onClick={onAutonomous}>
            <span className="experience-visual polished-visual activity-card-visual autonomous-card-visual" aria-hidden="true">
              <img className="activity-card-background" src="/assets/activity-cards/autonomous-assembly-in-progress-v3.jpg" alt="" />
            </span>
            <span className="experience-copy">
              <small>CREATE WITHOUT LIMITS</small>
              <strong>자율조립</strong>
              <p>부품 제한 없이 나만의 로봇을 조립하고 움직임과 물리를 시험합니다.</p>
              <em className="experience-card-action">
                <span>조립 시작</span>
                <span className="card-arrow"><Icon name="arrow" size={15} /></span>
              </em>
            </span>
          </button>

          <button className="experience-card mission" type="button" onClick={onMission}>
            <span className="experience-visual polished-visual activity-card-visual mission-card-visual" aria-hidden="true">
              <img className="activity-card-background" src="/assets/activity-cards/mission-training-arena-v7.jpg" alt="" />
            </span>
            <span className="experience-copy">
              <small>SOLVE THE CHALLENGE</small>
              <strong>미션</strong>
              <p>정해진 부품 수 안에서 로봇을 만들고 코스별 목표를 해결합니다.</p>
              <em className="experience-card-action">
                <span>미션 선택</span>
                <span className="card-arrow"><Icon name="arrow" size={15} /></span>
              </em>
            </span>
          </button>

          <button className="experience-card land" type="button" onClick={onWorld}>
            <span className="experience-visual polished-visual activity-card-visual world-card-visual" aria-hidden="true">
              <img className="activity-card-background" src="/assets/activity-cards/world-floating-biomes-restored-v6.jpg" alt="" />
            </span>
            <span className="experience-copy">
              <small>PLAY IN YOUR WORLD</small>
              <strong>월드</strong>
              <p>내 로봇을 캐릭터로 삼아 친구와 만나고 사용자 제작 월드와 게임을 즐깁니다.</p>
              <em className="experience-card-action">
                <span>월드 둘러보기</span>
                <span className="card-arrow"><Icon name="arrow" size={15} /></span>
              </em>
            </span>
          </button>

        </div>
      </div>
    </section>
  )
}

export function MissionHub({ onHome, onStart }: { onHome: () => void; onStart: () => void }) {
  const [selectedTrackId, setSelectedTrackId] = useState<MissionTrackId>('assembly')
  const [selectedStage, setSelectedStage] = useState(1)
  const { isAuthenticated, user } = useAuth()
  const { missions, progress, syncStatus, refresh } = useLandProgress()
  const selectedTrack = MISSION_TRACKS.find((track) => track.id === selectedTrackId) ?? MISSION_TRACKS[0]
  const selectedStageInfo = selectedTrack.stages[selectedStage - 1]
  const canStart = selectedStageInfo.playable === true

  useEffect(() => {
    if (isAuthenticated) void refresh()
  }, [isAuthenticated, refresh])

  const progressLabel = (stage: MissionStage, isAvailable: boolean) => {
    if (!isAvailable) return '미션 구성됨'
    if (!isAuthenticated || !stage.serverCode) return '도전 가능'
    const serverMission = missions.find((mission) => mission.code === stage.serverCode)
    const serverProgress = serverMission
      ? progress.find((item) => item.missionId === serverMission.id)
      : null
    const state = serverProgress?.state ?? serverMission?.state
    if (state === 'COMPLETED') return '완료'
    if (state === 'STARTED') return '진행 중'
    if (syncStatus === 'syncing') return '진행 확인 중'
    return '도전 가능'
  }

  return (
    <section className="portal-page mission-hub">
      <PortalHeader onHome={onHome} />
      <div className="mission-hub-content">
        <button className="portal-back" type="button" onClick={onHome}>← 활동 선택</button>
        <header className="mission-hub-heading">
          <div><span>MISSION CURRICULUM</span><h1>로봇 미션</h1><small>기초부터 응용까지 단계별로 배우는 로봇 과정</small></div>
          <p>초급 조립, 중급 코딩, 고급 응용 과정마다 기본 문제 10개가 제공되며 과정별 추가 문제도 계속 확장됩니다.</p>
        </header>

        <div className={`mission-progress-summary ${isAuthenticated ? 'member' : 'guest'}`} role="status">
          {isAuthenticated
            ? <><strong>{user?.name || '회원'}님의 진행 저장</strong><span>{syncStatus === 'syncing' ? '서버와 동기화 중입니다.' : '미션을 시작하면 서버에 진행 상태를 저장합니다.'}</span></>
            : <><strong>비회원 플레이</strong><span>로그인 없이도 현재 제공되는 기능과 미션을 회원과 동일하게 이용할 수 있습니다.</span></>}
        </div>

        <nav className="mission-track-tabs" aria-label="미션 과정">
          {MISSION_TRACKS.map((track, index) => (
            <button
              className={selectedTrack.id === track.id ? 'selected' : ''}
              data-track={track.id}
              type="button"
              aria-pressed={selectedTrack.id === track.id}
              key={track.id}
              onClick={() => {
                setSelectedTrackId(track.id)
                setSelectedStage(1)
              }}
            >
              <i>{index + 1}</i>
              <span><small>{track.eyebrow} · {track.level}</small><strong>{track.name}</strong><em>기본 10단계</em></span>
            </button>
          ))}
        </nav>

        <section className="mission-curriculum" data-track={selectedTrack.id}>
          <header>
            <div><span>{selectedTrack.eyebrow} COURSE</span><h2>{selectedTrack.name} <small>({selectedTrack.level})</small></h2></div>
            <p>{selectedTrack.description}</p>
          </header>
          <div className="mission-stage-list" aria-label={`${selectedTrack.name} 기본 문제`}>
            {selectedTrack.stages.map((stage, index) => {
              const stageNumber = index + 1
              const isAvailable = stage.playable === true
              return (
                <button
                  className={`${selectedStage === stageNumber ? 'selected' : ''} ${isAvailable ? 'available' : ''}`}
                  type="button"
                  key={stage.title}
                  aria-label={`${selectedTrack.name} ${stageNumber}단계 ${stage.title}`}
                  onClick={() => setSelectedStage(stageNumber)}
                >
                  <b>{String(stageNumber).padStart(2, '0')}</b>
                  <span><strong>{stage.title}</strong><small>{progressLabel(stage, isAvailable)}</small></span>
                </button>
              )
            })}
          </div>
        </section>

        <section className="mission-selected-stage" data-track={selectedTrack.id} aria-label="선택한 미션">
          <div>
            <span>{selectedTrack.level} · STAGE {String(selectedStage).padStart(2, '0')}</span>
            <h2>{selectedStageInfo.title}</h2>
            <p>{selectedStageInfo.objective}</p>
            <ul>
              {selectedStageInfo.rules.map((rule) => <li key={rule}>{rule}</li>)}
            </ul>
          </div>
          {canStart
            ? <button type="button" onClick={onStart}>미션 시작 <Icon name="arrow" size={15} /></button>
            : <span className="mission-coming-soon">콘텐츠 준비 중</span>}
        </section>

        <section className="mission-extra" data-track={selectedTrack.id} aria-label={`${selectedTrack.name} 추가 문제`}>
          <header>
            <div><span>EXTRA MISSIONS</span><h2>추가 문제</h2><p>기본 과정과 별도로 새로운 조건과 기록에 도전합니다.</p></div>
            <strong>추가 문제 {selectedTrack.extras.length}개</strong>
          </header>
          <div className="mission-extra-list">
            {selectedTrack.extras.map((mission, index) => (
              <article key={mission.title}>
                <b>EX {String(index + 1).padStart(2, '0')}</b>
                <span><strong>{mission.title}</strong><small>{mission.objective}</small></span>
              </article>
            ))}
          </div>
        </section>
      </div>
    </section>
  )
}

export function WorldPreview({ onHome }: { onHome: () => void }) {
  return (
    <section className="portal-page land-preview-page">
      <PortalHeader onHome={onHome} />
      <div className="land-preview-content">
        <button className="portal-back" type="button" onClick={onHome}>← 활동 선택</button>
        <div className="land-hero">
          <div className="land-hero-copy">
            <span>PINGPONG WORLD · IN DEVELOPMENT</span>
            <h1>내 로봇으로 살아가는<br />새로운 플레이 월드</h1>
            <p>자율조립에서 만든 로봇이 곧 나의 캐릭터가 됩니다. 다른 플레이어를 만나고, 함께 놀고, 누구나 만든 콘텐츠를 탐험하는 공간으로 확장됩니다.</p>
            <button type="button" onClick={onHome}>활동 선택으로 돌아가기</button>
          </div>
          <div className="land-world-preview" aria-hidden="true">
            <span className="world-island"><i /><i /><i /></span>
            <span className="world-avatar world-avatar-a"><img src="/assets/part-thumbnails/part-cube-stl-step.png" alt="" /><i /><i /><b /></span>
            <span className="world-avatar world-avatar-b"><img src="/assets/part-thumbnails/part-cube-stl-step.png" alt="" /><i /><i /><b /></span>
            <span className="world-beacon" />
          </div>
        </div>
        <div className="land-pillars">
          <article><span>01</span><strong>내 로봇이 캐릭터</strong><p>조립 구조와 움직임이 그대로 개성이 됩니다.</p></article>
          <article><span>02</span><strong>사람과 함께</strong><p>친구를 만나고 협동하거나 경쟁합니다.</p></article>
          <article><span>03</span><strong>사용자 제작 콘텐츠</strong><p>누구나 월드와 게임을 만들고 공유합니다.</p></article>
        </div>
      </div>
    </section>
  )
}
